import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TFMark } from '../TFLogo';
import { Lock, ArrowLeft, Sun, Moon } from '@phosphor-icons/react';

// ── Theme palettes ──────────────────────────────────────────────────────────
const PALETTES = {
  dark: {
    bg: '#0a0a0b',
    bgInk: '#000000',
    surface: '#111116',
    card: '#101014',
    text: '#f4f4f8',
    textMuted: '#b8b8c8',
    textFaint: '#6a6a78',
    border: '#2a2a30',
    borderFaint: '#1a1a20',
    accent: '#f5a623',
    accentInk: '#0a0a0a',
    green: '#00d68f',
    blue: '#4da6ff',
    red: '#ff4d4d',
    purple: '#a855f7',
    gridDot: 'rgba(255,255,255,0.022)',
    scanline: 'rgba(255,255,255,0.05)',
    overlayBg: 'rgba(10,10,11,0.9)',
  },
  light: {
    bg: '#F4F2EC',
    bgInk: '#FFFFFF',
    surface: '#FFFFFF',
    card: '#FBF9F4',
    text: '#002FA7',
    textMuted: 'rgba(0,47,167,0.7)',
    textFaint: 'rgba(0,47,167,0.45)',
    border: 'rgba(0,47,167,0.22)',
    borderFaint: 'rgba(0,47,167,0.10)',
    accent: '#C45C23',
    accentInk: '#ffffff',
    green: '#15803d',
    blue: '#002FA7',
    red: '#a32623',
    purple: '#7c3aed',
    gridDot: 'rgba(0,47,167,0.06)',
    scanline: 'rgba(0,47,167,0.06)',
    overlayBg: 'rgba(244,242,236,0.92)',
  },
};

const MODE_KEY = 'tf_private_theme_v1';

// PV_THEME exposes CSS variable refs — so any `style={{ color: PV_THEME.text }}`
// resolves to `var(--pv-text)`, which is set by PrivateLayout based on the
// active mode. No section file needs to change for theming to work.
export const PV_THEME = {
  bg: 'var(--pv-bg)',
  bgInk: 'var(--pv-bg-ink)',
  surface: 'var(--pv-surface)',
  card: 'var(--pv-card)',
  text: 'var(--pv-text)',
  textMuted: 'var(--pv-text-muted)',
  textFaint: 'var(--pv-text-faint)',
  border: 'var(--pv-border)',
  borderFaint: 'var(--pv-border-faint)',
  accent: 'var(--pv-accent)',
  accentInk: 'var(--pv-accent-ink)',
  green: 'var(--pv-green)',
  blue: 'var(--pv-blue)',
  red: 'var(--pv-red)',
  purple: 'var(--pv-purple)',
  gridDot: 'var(--pv-grid-dot)',
  scanline: 'var(--pv-scanline)',
  overlayBg: 'var(--pv-overlay-bg)',
};

function readMode() {
  try {
    const v = window.localStorage.getItem(MODE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* localStorage blocked — fall through to OS preference */
  }
  // First-time visitor: honour OS preference.
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch {
    /* matchMedia unavailable — fall through */
  }
  return 'dark';
}

function writeMode(m) {
  try {
    window.localStorage.setItem(MODE_KEY, m);
  } catch {
    /* noop */
  }
}

// Returns [mode, toggle] and applies CSS vars + body bg to <html data-pv-mode>
export function usePvMode() {
  const [mode, setMode] = useState(readMode);
  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      writeMode(next);
      return next;
    });
  }, []);

  // Follow OS theme changes for visitors who have not made an explicit choice.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => {
      let stored = null;
      try {
        stored = window.localStorage.getItem(MODE_KEY);
      } catch {
        /* ignore */
      }
      if (stored !== 'light' && stored !== 'dark') {
        setMode(e.matches ? 'light' : 'dark');
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    const p = PALETTES[mode];
    const root = document.documentElement;
    Object.entries({
      '--pv-bg': p.bg,
      '--pv-bg-ink': p.bgInk,
      '--pv-surface': p.surface,
      '--pv-card': p.card,
      '--pv-text': p.text,
      '--pv-text-muted': p.textMuted,
      '--pv-text-faint': p.textFaint,
      '--pv-border': p.border,
      '--pv-border-faint': p.borderFaint,
      '--pv-accent': p.accent,
      '--pv-accent-ink': p.accentInk,
      '--pv-green': p.green,
      '--pv-blue': p.blue,
      '--pv-red': p.red,
      '--pv-purple': p.purple,
      '--pv-grid-dot': p.gridDot,
      '--pv-scanline': p.scanline,
      '--pv-overlay-bg': p.overlayBg,
    }).forEach(([k, v]) => root.style.setProperty(k, v));
    root.setAttribute('data-pv-mode', mode);
    document.body.style.background = p.bg;
    document.body.style.color = p.text;
    return () => {
      // Clean up so the rest of the app isn't tinted
      document.body.style.background = '';
      document.body.style.color = '';
      root.removeAttribute('data-pv-mode');
    };
  }, [mode]);

  return [mode, toggle];
}

// ── Shared header / shell ───────────────────────────────────────────────────
export function PrivateLayout({ children, eyebrowLabel, showBack = true, testid = 'private-shell' }) {
  const [mode, toggleMode] = usePvMode();
  const isDark = mode === 'dark';

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ background: PV_THEME.bg, color: PV_THEME.text }}
      data-testid={testid}
      data-pv-mode={mode}
    >
      {/* Radial dot grid */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage: `radial-gradient(circle, ${PV_THEME.gridDot} 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
        }}
        aria-hidden
      />
      {/* Scan line */}
      <div
        className="pointer-events-none fixed inset-x-0 h-0.5 z-[60]"
        style={{ background: PV_THEME.scanline, animation: 'pvScan 9s linear infinite' }}
        aria-hidden
      />

      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b"
        style={{ background: PV_THEME.overlayBg, borderColor: PV_THEME.borderFaint }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <Link to="/private" className="flex items-center gap-3 sm:gap-5 min-h-[44px]" data-testid="pv-home-link">
            <TFMark color={PV_THEME.text} size={22} />
            <span
              className="hidden xs:inline font-machina font-bold text-[10px] sm:text-[11px] uppercase whitespace-nowrap"
              style={{ letterSpacing: '0.9em', paddingLeft: '0.9em' }}
            >
              TRUSTFRAME
            </span>
            <span
              className="hidden md:inline font-mono text-[9px] tracking-[0.25em] uppercase pl-4 border-l"
              style={{ color: PV_THEME.textFaint, borderColor: PV_THEME.border }}
            >
              {eyebrowLabel || 'Private View · MMXXVI'}
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggleMode}
              data-testid="pv-theme-toggle"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-2 border transition-colors hover:opacity-100 opacity-80"
              style={{ borderColor: PV_THEME.border, color: PV_THEME.text }}
            >
              {isDark ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
            </button>

            <span
              className="inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] uppercase"
              style={{ color: PV_THEME.accent }}
            >
              <Lock size={11} weight="fill" /> <span className="hidden sm:inline">PRIVATE</span>
            </span>
            {showBack ? (
              <Link
                to="/private"
                data-testid="pv-back-link"
                className="min-h-[36px] px-2 sm:px-3 inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] uppercase opacity-70 hover:opacity-100 transition-opacity"
                style={{ color: PV_THEME.text }}
              >
                <ArrowLeft size={12} weight="bold" /> <span className="hidden sm:inline">Index</span>
              </Link>
            ) : (
              <Link
                to="/"
                data-testid="pv-public-link"
                className="min-h-[36px] px-2 sm:px-3 inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] uppercase opacity-70 hover:opacity-100 transition-opacity"
                style={{ color: PV_THEME.text }}
              >
                <ArrowLeft size={12} weight="bold" /> <span className="hidden sm:inline">Public</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-14">{children}</main>

      <style>{`
        @keyframes pvScan {
          0% { transform: translateY(0); }
          100% { transform: translateY(100vh); }
        }
        @keyframes pvBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes pvFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pvFlow {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes pvPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
